import * as React from "react";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Grid from '@mui/material/Grid';
import TableRowType from "../types/TableRowType";
import colors from "../styles/colors";
import { CircularProgress, styled, TableFooter } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronUp, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import {
  StyledMaterialInnerTable,
  StyledMaterialTable,
  StyledMaterialTableWrapper,
  StyledSpinnerWrapper,
  StyledSyncIcon,
} from "../styles/styledComponents";
import { useSelectedContextState } from "../context/selectedContext";
import { useTranslation } from "react-i18next";
import { StyledLink } from "../styles/styledComponents";
import NumberFormat from 'react-number-format';
import { useEffect } from "react";
import { generateSummaryTablePdf, generateCombinedSummaryTablePdf } from "../helpers/PdfHelper";
import { formatWeight, formatNumber, detectBestLocale } from '../utils/utils';

export const StyledLeftSpinnerWrapper = styled(StyledSpinnerWrapper)`
  justify-content: flex-start;
  padding-left: 50px;
  margin-bottom: 30px;
`;

function Row(props: { row: TableRowType }) {
  const { row } = props;
  const [open, setOpen] = React.useState(false);
  const { t } = useTranslation("trans");
  const [state, dispatch] = useSelectedContextState();
  const { summaryTable } = state;
  const locale = detectBestLocale();
  
  const removeRow = (row: TableRowType) => {
    dispatch({ type: "setSummaryTableLoading", payload: true });
    let list = summaryTable.filter((x) => x !== row);
    dispatch({ type: "setSummaryTable", payload: list });
  };
  
  const rowPrice = row.row.reduce((a, b) => a + (b.netPrice ? b.netPrice : 0), 0);
  const rowWeight = row.row.reduce((a, b) => a + (b.weight ? b.weight : 0), 0);

  return (
    <React.Fragment>
      <TableRow
        className={open ? "Active" : ""}
        style={{ border: "none" }}
        sx={{ "& > *": { borderBottom: "unset" } }}
      >
        <TableCell
          style={{
            border: "none",
            color: open ? colors.bgPrimary : colors.txtPrimary,
          }}
          colSpan={6}
        >
          <IconButton
            style={{ marginRight: "20px" }}
            aria-label="expand row"
            size="small"
            className={!open ? "NotActive" : ""}
            onClick={() => setOpen(!open)}
          >
            {open ? (
              <FontAwesomeIcon icon={faChevronUp} />
            ) : (
              <FontAwesomeIcon icon={faChevronDown} />
            )}
          </IconButton>
          {row.name}
        </TableCell>

        <TableCell
          width="10%"
          style={{
            border: "none",
            color: open ? colors.bgPrimary : colors.txtPrimary,
          }}
          align="right"
        >
          {/*<NumberFormat value={rowPrice} displayType={'text'} decimalScale={2} decimalSeparator={','} thousandSeparator={false} fixedDecimalScale={true} />*/}
            {formatNumber(rowPrice, locale, 0)}
        </TableCell>
        <TableCell
          style={{
            width: "20px",
            border: "none",
          }}
        >
          <FontAwesomeIcon
            style={{ cursor: "pointer" }}
            color={open ? colors.bgSecondary : colors.ErrorBorder}
            icon={faXmark}
            size="lg"
            onClick={() => removeRow(row)}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ padding: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <StyledMaterialInnerTable aria-label="summary">
              <TableHead>
                <TableRow>
                  <TableCell>{t("table.artNr")}</TableCell>
                  <TableCell>{t("table.term")}</TableCell>
                  <TableCell align="left">{t("table.qty")}</TableCell>
                  <TableCell align="left">{t("table.discount")}</TableCell>
                  <TableCell align="left">{t("table.price/art")}</TableCell>
                  <TableCell align="left">{t("table.weight")}</TableCell>
                  <TableCell width="10%" align="left">
                    {t("table.net_price")}
                  </TableCell>
                  <TableCell
                    style={{
                      width: "20px",
                    }}
                  ></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {row.row.map((r, index) => (
                  <TableRow key={r.art + index}>
                    <TableCell> {r.art}</TableCell>
                    <TableCell>{r.term}</TableCell>
                    <TableCell align="left">{r.amount}</TableCell>
                    <TableCell align="left">{r.discount}</TableCell>
                    <TableCell align="left">
                      <NumberFormat value={r.fullPrice} displayType={'text'} decimalScale={2} decimalSeparator={','} thousandSeparator={false} fixedDecimalScale={true} />
                    </TableCell>
                    <TableCell align="left"><NumberFormat value={r.weight} displayType={'text'} decimalScale={2} decimalSeparator={','} thousandSeparator={false} fixedDecimalScale={true} /></TableCell>
                    <TableCell colSpan={2} align="left">
                      <NumberFormat value={r.netPrice} displayType={'text'} decimalScale={2} decimalSeparator={','} thousandSeparator={false} fixedDecimalScale={true} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={5}>{t("table.summary")}</TableCell>
                  <TableCell
                    style={{
                      textAlign: "left",
                    }}
                  >
                    <NumberFormat value={rowWeight} displayType={'text'} decimalScale={2} decimalSeparator={','} thousandSeparator={false} fixedDecimalScale={true} />&nbsp;KG
                  </TableCell>
                  <TableCell
                    colSpan={2}
                    style={{
                      textAlign: "left",
                    }}
                  >
                    <NumberFormat value={rowPrice} displayType={'text'} decimalScale={2} decimalSeparator={','} thousandSeparator={false} fixedDecimalScale={true} />
                  </TableCell>
                </TableRow>
              </TableFooter>
            </StyledMaterialInnerTable>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export default function SummaryTable(props: { countryCode?: string }) {
  const [state, dispatch] = useSelectedContextState();
  const { summaryTable, summaryTableLoading } = state;
  const { t } = useTranslation("trans");
  const { countryCode } = props;
  const [isLoadingPdf, setLoadingPdf] = React.useState(false);
  const [isLoadingCombinedPdf, setLoadingCombinedPdf] = React.useState(false);
  const locale = detectBestLocale();
  const triggerPdfDownload = (combined: boolean) => {
    if (combined) {
      setLoadingCombinedPdf(true);
      const fileName = t("pdf.combinedSummaryTableFileName") + "_" + new Date().toLocaleDateString() + ".pdf";
      generateCombinedSummaryTablePdf({ summaryTable, countryCode }, fileName).then(() => setLoadingCombinedPdf(false));
    }
    else {
      setLoadingPdf(true);
      const fileName = t("pdf.summaryTableFileName") + "_" + new Date().toLocaleDateString() + ".pdf";
      generateSummaryTablePdf({ summaryTable, countryCode }, fileName).then(() => setLoadingPdf(false));
    }
  };

  const summaryTotalTable = summaryTable.reduce((a, b) => a + (b.netPrice ? b.netPrice : 0), 0);
  
  useEffect(() => {
    dispatch({ type: "setSummaryTableLoading", payload: false });
  }, [summaryTable]);

  if (summaryTableLoading) return <StyledLeftSpinnerWrapper>
    <CircularProgress color="inherit" />
    {t("loading")}
  </StyledLeftSpinnerWrapper>;

  return (
    <StyledMaterialTableWrapper>
      <StyledMaterialTable aria-label="collapsible table">
        <TableHead>
          <TableRow>
            <TableCell colSpan={6}>
              {t("table.designation").toUpperCase()}
            </TableCell>
            <TableCell colSpan={1} width="10%" align="right">
              {t("table.net_price").toUpperCase()}
            </TableCell>
            <TableCell colSpan={1} width="20px">
              &nbsp;
            </TableCell>            
          </TableRow>
        </TableHead>
        <TableBody>
          {summaryTable.map((row, index) => (
            <Row key={row.name + index} row={row} />
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={6}>{t("table.totalSummary")}</TableCell>
            <TableCell colSpan={1} style={{ textAlign: "right" }}>
              {/*<NumberFormat value={summaryTotalTable} displayType={'text'} decimalScale={2} decimalSeparator={','} thousandSeparator={false} fixedDecimalScale={true} />*/}
              {formatNumber(summaryTotalTable, locale, 0)}
            </TableCell>
            <TableCell colSpan={1}>&nbsp;</TableCell>            
          </TableRow>
        </TableFooter>
      </StyledMaterialTable>
      {(summaryTable && summaryTable.length > 0) ? (
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems="flex-end"
        >
          <StyledLink target={"_blank"} onClick={() => { triggerPdfDownload(false); }}>
            {isLoadingPdf ? (
              <StyledSyncIcon icon={faSpinner} size="lg" className="fas fa-refresh fa-spin" />
            ) : t('buttons.save_as_pdf').toUpperCase()}
          </StyledLink>
          <StyledLink target={"_blank"} onClick={() => { triggerPdfDownload(true); }}>
            {isLoadingCombinedPdf ? (
              <StyledSyncIcon icon={faSpinner} size="lg" className="fas fa-refresh fa-spin" />
            ) : t('buttons.save_as_combined_pdf').toUpperCase()}
          </StyledLink></Grid>)
        : null}
    </StyledMaterialTableWrapper>
  );
}
